import { useCallback, type ReactNode } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { Content, ContentHeader, SupportButton } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { EntityListProvider } from '@backstage/plugin-catalog-react';
import { scaffolderPlugin, scaffolderTranslationRef } from '@backstage/plugin-scaffolder';
import type { TemplateListPageProps } from '@backstage/plugin-scaffolder/alpha';
import type { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { CustomTemplateCard } from './CustomTemplateCard';
import { FilteredTemplates } from './FilteredTemplates';

export const TemplateList = ({
  templateFilter,
}: {
  templateFilter?: TemplateListPageProps['templateFilter'];
}) => {
  const navigate = useNavigate();
  const { t } = useTranslationRef(scaffolderTranslationRef);
  const templateRoute = useRouteRef(scaffolderPlugin.routes.selectedTemplate);

  const onTemplateSelected = useCallback(
    (template: TemplateEntityV1beta3) => {
      const { namespace, name } = parseEntityRef(stringifyEntityRef(template));
      navigate(templateRoute({ namespace, templateName: name }));
    },
    [navigate, templateRoute],
  );

  return (
    <>
      <ContentHeader>
        <SupportButton>{t('templateListPage.contentHeader.supportButtonTitle')}</SupportButton>
      </ContentHeader>
      <FilteredTemplates
        templateFilter={templateFilter}
        TemplateCardComponent={CustomTemplateCard}
        onTemplateSelected={onTemplateSelected}
      />
    </>
  );
};

/** Our template list on the native templates tab; every other path, such as the wizard, stays native. */
export const NfsTemplatesRoutes = ({ nativeTemplatesPage }: { nativeTemplatesPage: ReactNode }) => (
  <Routes>
    <Route
      index
      element={
        <EntityListProvider>
          <Content>
            <TemplateList />
          </Content>
        </EntityListProvider>
      }
    />
    <Route path="*" element={nativeTemplatesPage} />
  </Routes>
);
